import { forwardRef, useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/ui';
import { BarChartIcon, AlertCircle, FileCheckIcon, TornadoIcon, InfoIcon, WatchIcon } from 'lucide-react';
import { DropletIcon, ClipboardCheckIcon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui';
import { cn } from '@shadcn/utils';
import { type ExtendedBuildingInfoData } from 'app/utils/constants/buildingInfoStructure';
import { Documents } from 'app/types/visualizations';
import { ComplianceHistoryItem } from 'app/utils/constants/buildingInfoStructure';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import userServices from 'app/services/user-services';

interface TeamMember {
  id: string;
  name: string;
}

export interface ComplianceYearProps {
  buildingInfo: ExtendedBuildingInfoData & {
    complianceHistory?: ComplianceHistoryItem[];
  };
  meterReadings: Documents;
}

// Add this helper function at the top level after other imports
const getDaysUntilNextMayFirst = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const nextDeadline = new Date(currentYear, 4, 1); // Month is 0-based, so 4 = May

  // If we've passed this year's deadline, use next year's
  if (today > nextDeadline) {
    nextDeadline.setFullYear(currentYear + 1);
  }

  const diffTime = nextDeadline.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Add this helper function after getDaysUntilNextMayFirst
const getPastYear = () => {
  const currentYear = new Date().getFullYear();
  const pastYear = currentYear - 1;
  return pastYear;
};

// Helper to get the most recent compliance status
const getMostRecentCompliance = (complianceHistory: ComplianceHistoryItem[] = [], type: 'll84' | 'll97') => {
  console.log('complianceHistory', complianceHistory);
  return complianceHistory
    ?.filter((history) => history.complianceType === type)
    .sort((a, b) => new Date(b.confirmedAt).getTime() - new Date(a.confirmedAt).getTime())[0];
};

// Helper to render compliance status
const ComplianceStatus = ({
  status,
  confirmedBy,
  confirmedAt,
  teamMembers,
  paymentConfirmationNumber,
}: {
  status: 'confirmed' | 'passed' | 'failed' | undefined;
  confirmedBy?: string;
  confirmedAt?: string;
  teamMembers: TeamMember[];
  paymentConfirmationNumber?: string;
}) => {
  let confirmationStatus = !status ? 'pending' : status;

  const statusConfig = {
    confirmed: { color: 'text-blue-500 bg-blue-500/10', icon: ClipboardCheckIcon },
    passed: { color: 'text-green-500 bg-green-500/10', icon: CheckCircle2Icon },
    failed: { color: 'text-destructive bg-destructive/10', icon: XCircleIcon },
    pending: { color: 'text-orange-500 bg-orange/10', icon: WatchIcon },
  };

  const config = statusConfig[confirmationStatus];
  const Icon = config.icon;

  const formattedDate = confirmedAt ? new Date(confirmedAt).toLocaleDateString('en-US') : '';
  const confirmedByUser = teamMembers.find((member) => member.id === confirmedBy)?.name || 'Unknown User';
  const statusDetail =
    status === 'confirmed' && confirmedBy && confirmedAt ? `by ${confirmedByUser}, ${formattedDate}` : '';
  const statusElement = (
    <div className={cn('flex items-center gap-2 rounded-full px-3 py-1', config.color)}>
      <Icon className="h-4 w-4" />
      <span className="text-sm capitalize">
        {status} {statusDetail}
      </span>
    </div>
  );

  return (
    <div className="space-y-2">
      {statusElement}
      <div className="ml-1 h-[22px]">
        {paymentConfirmationNumber && (
          <span className="rounded-md bg-muted/30 px-2 py-0.5 text-sm">DOB Payment #{paymentConfirmationNumber}</span>
        )}
      </div>
    </div>
  );
};

// Add this helper function after other helper functions
const truncateToOneDecimal = (num: number | undefined) => {
  if (num === undefined) return '0.0';
  return (Math.trunc(num * 10) / 10).toFixed(1);
};

export const ComplianceYear = forwardRef<HTMLDivElement, ComplianceYearProps>(
  ({ buildingInfo, meterReadings }, ref) => {
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
    const { authUser } = useJumboAuth();
    const organizationId = authUser?.relationships?.organization?.data?.id;

    useEffect(() => {
      const fetchTeamMembers = async () => {
        if (!organizationId) return;

        setIsLoadingTeamMembers(true);
        try {
          const data = await userServices.getTeamMembers(organizationId);
          const formattedMembers = data.map((member) => ({
            id: member.id,
            name: member.attributes?.firstName
              ? member.attributes.firstName.charAt(0).toUpperCase() + member.attributes.firstName.slice(1)
              : 'Unknown User',
          }));
          setTeamMembers(formattedMembers);
        } catch (error) {
          console.error('Error fetching team members:', error);
        } finally {
          setIsLoadingTeamMembers(false);
        }
      };

      fetchTeamMembers();
    }, [organizationId]);

    const daysRemaining = getDaysUntilNextMayFirst();
    const isUrgent = daysRemaining <= 30;
    const showCountdown =
      buildingInfo.complianceRequirements.ll84.required || buildingInfo.complianceRequirements.ll97.required;

    const { ll84Compliance, ll97Compliance } = useMemo(
      () => ({
        ll84Compliance: getMostRecentCompliance(buildingInfo.complianceHistory || [], 'll84'),
        ll97Compliance: getMostRecentCompliance(buildingInfo.complianceHistory || [], 'll97'),
      }),
      [buildingInfo.complianceHistory]
    );

    return (
      <Card
        ref={ref}
        className="min-h-[550px] transform rounded-xl border-2 border-gray-500 bg-gradient-to-br from-background/80 to-background/40 p-1 shadow-xl transition-all duration-200 hover:shadow-2xl"
      >
        <CardHeader className="pb-4 pt-6">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-lg font-semibold text-foreground/90">
              <FileCheckIcon className="h-5 w-5 text-primary/90" />
              Compliance Year {getPastYear()}
            </div>
            {showCountdown ? (
              <div
                className={cn(
                  'flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium',
                  isUrgent ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                )}
              >
                <AlertCircle className="h-4 w-4" />
                {daysRemaining} days remaining
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-full bg-muted/20 px-3 py-1 text-sm font-medium text-muted-foreground">
                <InfoIcon className="h-4 w-4" />
                Compliance for this property is not required
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Energy Benchmarking Section */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 rounded-lg bg-background/50 p-4">
              <div className="flex items-center gap-2">
                <BarChartIcon className="h-5 w-5 text-primary/90" />
                <h3 className="text-base font-medium">Energy Benchmarking</h3>
              </div>
              {buildingInfo.complianceRequirements.ll84.required && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 rounded-full bg-muted/20 px-3 py-1">
                      <span className="text-sm text-muted-foreground">LL84</span>
                      {buildingInfo.complianceRequirements.ll84.requiredToReportWater && (
                        <div className="inline-flex items-center gap-1.5">
                          <DropletIcon className="h-3.5 w-3.5 text-blue-400" />
                          <p className="text-xs font-medium text-blue-400">Water Reporting Required</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {ll84Compliance && (
                    <div className="ml-1">
                      <ComplianceStatus
                        status={ll84Compliance.complianceStatus}
                        confirmedBy={ll84Compliance.confirmedBy}
                        confirmedAt={ll84Compliance.confirmedAt}
                        teamMembers={teamMembers}
                        paymentConfirmationNumber={ll84Compliance.paymentConfirmationNumber}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="overflow-hidden rounded-lg border border-border/20">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-background/10">
                    <th className="w-[20%] p-2 text-left text-xs font-medium text-muted-foreground">SERVICE</th>
                    <th className="w-[30%] p-2 text-right text-xs font-medium text-muted-foreground">USAGE</th>
                    <th className="w-[25%] p-2 text-right text-xs font-medium text-muted-foreground">KBTU</th>
                    <th className="w-[25%] p-2 text-right text-xs font-medium text-muted-foreground">EMISSIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries({
                    Electric: {
                      value:
                        meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.electric?.totalUsage,
                      kbtu: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.electric?.totalKbtu,
                      unit: 'kWh',
                      emissions:
                        meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.electric?.emissions,
                    },
                    Gas: {
                      value: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.gas?.totalUsage,
                      kbtu: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.gas?.totalKbtu,
                      unit: 'Therms',
                      emissions: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.gas?.emissions,
                    },
                    Oil: {
                      value: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.oil?.totalUsage,
                      kbtu: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.oil?.totalKbtu,
                      unit: 'Gallons',
                      emissions: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.oil?.emissions,
                    },
                    Steam: {
                      value: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.steam?.totalUsage,
                      kbtu: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.steam?.totalKbtu,
                      unit: 'MLBS',
                      emissions:
                        meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.steam?.emissions,
                    },
                    Water: {
                      value: meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.water?.totalUsage,
                      kbtu: 'N/A',
                      unit: 'CCF',
                      emissions:
                        meterReadings?.yearlyData?.[getPastYear()]?.energyUsageTotalsByService?.water?.emissions,
                    },
                  }).map(([service, data]) => (
                    <tr key={service} className="hover:bg-background/5">
                      <td className="border-b border-border/30 p-2 font-medium">{service}</td>
                      <td className="border-b border-border/30 p-2 text-right">
                        {data.value?.toLocaleString() || '0'} {data.unit}
                      </td>
                      <td className="border-b border-border/30 p-2 text-right">{data.kbtu?.toLocaleString() || '0'}</td>
                      <td className="border-b border-border/30 p-2 text-right">
                        {data.emissions?.toLocaleString() || '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Emissions Reporting Section */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 rounded-lg bg-background/50 p-4">
              <div className="flex items-center gap-2">
                <TornadoIcon className="h-5 w-5 text-primary/90" />
                <h3 className="text-base font-medium">Emissions Reporting</h3>
              </div>
              {buildingInfo.complianceRequirements.ll97.required && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 rounded-full bg-muted/20 px-3 py-1">
                      <span className="text-sm text-muted-foreground">LL97</span>
                      <div className="inline-flex items-center gap-1.5">
                        <ClipboardCheckIcon className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-medium text-primary">
                          {buildingInfo.complianceRequirements.ll97.reportType
                            ? buildingInfo.complianceRequirements.ll97.reportType.charAt(0).toUpperCase() +
                              buildingInfo.complianceRequirements.ll97.reportType.slice(1)
                            : 'Simple'}{' '}
                          Report
                        </span>
                      </div>
                    </div>
                  </div>
                  {ll97Compliance && (
                    <div className="ml-1">
                      <ComplianceStatus
                        status={ll97Compliance.complianceStatus}
                        confirmedBy={ll97Compliance.confirmedBy}
                        confirmedAt={ll97Compliance.confirmedAt}
                        teamMembers={teamMembers}
                        paymentConfirmationNumber={ll97Compliance.paymentConfirmationNumber}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="overflow-hidden rounded-lg border border-border/20">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-background/10">
                    <th className="w-[50%] p-2 text-left text-xs font-medium text-muted-foreground">EMISSIONS</th>
                    <th className="w-[50%] p-2 text-right text-xs font-medium text-muted-foreground">VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-background/5">
                    <td className="border-b border-border/30 p-3 font-medium">Actual Emissions</td>
                    <td className="border-b border-border/30 p-3 text-right font-medium">
                      {truncateToOneDecimal(meterReadings?.yearlyData?.[getPastYear()]?.actualEmissions)} tCO2e
                    </td>
                  </tr>
                  {showCountdown && (
                    <>
                      <tr className="hover:bg-background/5">
                        <td className="border-b border-border/30 p-3 font-medium">Threshold Limit</td>
                        <td className="border-b border-border/30 p-3 text-right font-medium">
                          {truncateToOneDecimal(meterReadings?.yearlyData?.[getPastYear()]?.threshold)} tCO2e
                        </td>
                      </tr>
                      <tr className="hover:bg-background/5">
                        <td className="border-b border-border/30 p-3 font-medium">Delta</td>
                        <td
                          className={cn('border-b border-border/30 p-3 text-right font-medium', {
                            'text-destructive':
                              meterReadings?.yearlyData?.[getPastYear()]?.threshold &&
                              meterReadings?.yearlyData?.[getPastYear()]?.actualEmissions &&
                              meterReadings.yearlyData[getPastYear()].threshold -
                                meterReadings.yearlyData[getPastYear()].actualEmissions <
                                0,
                            'text-green-500':
                              meterReadings?.yearlyData?.[getPastYear()]?.threshold &&
                              meterReadings?.yearlyData?.[getPastYear()]?.actualEmissions &&
                              meterReadings.yearlyData[getPastYear()].threshold -
                                meterReadings.yearlyData[getPastYear()].actualEmissions >=
                                0,
                          })}
                        >
                          {meterReadings?.yearlyData?.[getPastYear()]?.threshold &&
                          meterReadings?.yearlyData?.[getPastYear()]?.actualEmissions
                            ? `${truncateToOneDecimal(
                                meterReadings.yearlyData[getPastYear()].threshold -
                                  meterReadings.yearlyData[getPastYear()].actualEmissions
                              )} tCO2e`
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr className="hover:bg-background/5">
                        <td className="border-b border-border/30 p-3 font-medium">Annual Penalty</td>
                        <td className="border-b border-border/30 p-3 text-right font-medium text-destructive">
                          {meterReadings?.yearlyData?.[getPastYear()]?.estimatedAnnualPenalty
                            ? `$${meterReadings.yearlyData[getPastYear()].estimatedAnnualPenalty.toLocaleString()}`
                            : 'N/A'}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);
